import React, { useState, useEffect, useMemo } from 'react';
import { ShieldCheck, User, Mail, FileText, Globe, Clock, AlertCircle, CheckCircle, Loader2, ArrowRight, ChevronLeft, ChevronRight, Lock } from 'lucide-react';

type ViewMode = 'day' | 'week' | 'month';

interface AgentProfile {
  agentUid: string;
  agentName: string;
  agentTitle: string;
  profilePhotoUrl?: string;
  bookingAccessEnabled: boolean;
  timezone?: string;
}

export default function PublicAgentBookingView({ agentUid }: { agentUid: string }) {
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [agentError, setAgentError] = useState<string | null>(null);

  const [step, setStep] = useState<'verify' | 'select_slot' | 'confirmation'>('verify');
  const [form, setForm] = useState({
    name: '',
    email: '',
    applicationId: '',
  });
  const [canonicalAppId, setCanonicalAppId] = useState('');
  const [applicantName, setApplicantName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Timezone and Slot state
  const [userTimezone] = useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  });
  const [slots, setSlots] = useState<{ dateTime: string; durationMinutes: number }[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [slotDuration, setSlotDuration] = useState<number>(30);
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookedDetails, setBookedDetails] = useState<any>(null);

  // View mode & date navigation
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Auto-fill query parameters if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const appIdParam = params.get('appId') || params.get('applicationId');
    if (emailParam || appIdParam) {
      setForm(prev => ({
        ...prev,
        email: emailParam ? decodeURIComponent(emailParam).trim() : prev.email,
        applicationId: appIdParam ? decodeURIComponent(appIdParam).trim() : prev.applicationId,
      }));
    }
  }, []);

  // Fetch Agent Profile on mount
  useEffect(() => {
    (async () => {
      setLoadingAgent(true);
      setAgentError(null);
      try {
        const res = await fetch(`/api/public/booking/agent/${encodeURIComponent(agentUid)}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Team member not found.');
        }
        setAgentProfile(data);
      } catch (err: any) {
        setAgentError(err.message || 'Failed to load team member booking details.');
      } finally {
        setLoadingAgent(false);
      }
    })();
  }, [agentUid]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setVerifyError(null);
    try {
      const res = await fetch('/api/public/screening/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          applicationId: form.applicationId.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'We could not verify your application details. Please check your Email Address and Application ID and try again.');
      }

      setCanonicalAppId(data.applicationId);
      setApplicantName(data.applicantName || form.name || 'Applicant');
      setStep('select_slot');
      fetchSlots(form.email.trim(), data.applicationId);
    } catch (err: any) {
      setVerifyError(err.message || 'We could not verify your application details. Please check your Email Address and Application ID and try again.');
    } finally {
      setVerifying(false);
    }
  };

  const fetchSlots = async (email: string, appId: string) => {
    setLoadingSlots(true);
    try {
      const res = await fetch(`/api/public/booking/agent/${encodeURIComponent(agentUid)}/slots?email=${encodeURIComponent(email)}&applicationId=${encodeURIComponent(appId)}&timezone=${encodeURIComponent(userTimezone)}`);
      const data = await res.json();
      if (res.ok) {
        setSlots(data.slots || []);
        if (data.durationMinutes) setSlotDuration(data.durationMinutes);
      } else if (res.status === 403) {
        setVerifyError(data.error || 'Booking access is currently disabled for this team member.');
        setStep('verify');
      }
    } catch (err) {
      console.error('Failed to load slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    setBookingError(null);
    try {
      const res = await fetch(`/api/public/booking/agent/${encodeURIComponent(agentUid)}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim() || applicantName,
          email: form.email.trim(),
          applicationId: canonicalAppId,
          dateTime: selectedSlot,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to complete booking.');
      }

      setBookedDetails({
        dateTime: selectedSlot,
        assignedAgent: data.assignedAgent || agentProfile?.agentName,
        meetingUrl: data.meetingUrl,
        eventId: data.eventId,
      });
      setStep('confirmation');
    } catch (err: any) {
      setSelectedSlot(null);
      setBookingError(err.message || 'This time slot is no longer available. Please select another available slot.');
      fetchSlots(form.email.trim(), canonicalAppId);
    } finally {
      setBooking(false);
    }
  };

  // Group slots by Date string (YYYY-MM-DD)
  const groupedSlotsByDate = useMemo(() => {
    const map: Record<string, { dateTime: string; durationMinutes: number }[]> = {};
    slots.forEach(s => {
      const d = new Date(s.dateTime);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(s);
    });
    return map;
  }, [slots]);

  // Date Navigation handlers
  const handlePrevDate = () => {
    const next = new Date(currentDate);
    if (viewMode === 'day') next.setDate(next.getDate() - 1);
    else if (viewMode === 'week') next.setDate(next.getDate() - 7);
    else if (viewMode === 'month') next.setMonth(next.getMonth() - 1);
    setCurrentDate(next);
  };

  const handleNextDate = () => {
    const next = new Date(currentDate);
    if (viewMode === 'day') next.setDate(next.getDate() + 1);
    else if (viewMode === 'week') next.setDate(next.getDate() + 7);
    else if (viewMode === 'month') next.setMonth(next.getMonth() + 1);
    setCurrentDate(next);
  };

  // Calculate visible dates
  const visibleDates = useMemo(() => {
    const dates: Date[] = [];
    const base = new Date(currentDate);

    if (viewMode === 'day') {
      dates.push(new Date(base));
    } else if (viewMode === 'week') {
      const dayOfWeek = base.getDay();
      const sunday = new Date(base);
      sunday.setDate(base.getDate() - dayOfWeek);
      for (let i = 0; i < 7; i++) {
        const d = new Date(sunday);
        d.setDate(sunday.getDate() + i);
        dates.push(d);
      }
    } else if (viewMode === 'month') {
      const year = base.getFullYear();
      const month = base.getMonth();
      const lastDay = new Date(year, month + 1, 0);
      for (let d = 1; d <= lastDay.getDate(); d++) {
        dates.push(new Date(year, month, d));
      }
    }
    return dates;
  }, [currentDate, viewMode]);

  // Format interval string (e.g. "9:00 AM - 9:30 AM")
  const formatSlotInterval = (isoString: string, durationMinutes: number) => {
    const start = new Date(isoString);
    const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    const formatTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  if (loadingAgent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
          <p className="text-xs font-semibold text-slate-500">Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (agentError || !agentProfile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center space-y-4 border border-slate-200">
          <div className="h-12 w-12 bg-rose-100 text-rose-600 rounded-2xl flex items-center justify-center mx-auto">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-black text-slate-900">Booking Page Unavailable</h2>
          <p className="text-xs text-slate-500">{agentError || 'Team member details could not be found.'}</p>
        </div>
      </div>
    );
  }

  // Handle Disabled State
  if (agentProfile.bookingAccessEnabled === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6 font-sans">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-slate-200">
          <div className="bg-surface px-6 py-5 text-text-primary flex items-center gap-3 border-b border-border-subtle">
            <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-200">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-text-primary">Direct Booking Access Disabled</h1>
              <p className="text-xs text-text-tertiary">Team Member: {agentProfile.agentName}</p>
            </div>
          </div>
          <div className="p-6 sm:p-8 space-y-5 text-center">
            <div className="h-16 w-16 bg-amber-50 border border-amber-200 text-amber-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xs">
              <Lock className="h-8 w-8" />
            </div>
            <div className="space-y-1.5">
              <h2 className="text-lg font-black text-slate-900">Public Bookings Suspended</h2>
              <p className="text-xs text-slate-600 leading-relaxed max-w-md mx-auto">
                Unique booking access for <strong>{agentProfile.agentName}</strong> ({agentProfile.agentTitle}) is currently disabled by workspace administration.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-slate-500 space-y-2 text-left">
              <p className="font-bold text-slate-700">Important Note:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Existing scheduled meetings and bookings remain completely intact.</li>
                <li>New public bookings through this link are currently paused.</li>
                <li>If you need to schedule a meeting, please contact support or your program coordinator.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-3 sm:p-6 font-sans">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden border border-slate-200">
        {/* Top Header with Agent Profile */}
        <div className="bg-surface px-6 py-5 text-text-primary flex items-center justify-between border-b border-border-subtle">
          <div className="flex items-center gap-3">
            {agentProfile.profilePhotoUrl ? (
              <img src={agentProfile.profilePhotoUrl} alt={agentProfile.agentName} className="h-11 w-11 rounded-xl object-cover border border-border-subtle" />
            ) : (
              <div className="h-11 w-11 rounded-xl bg-brand-primary flex items-center justify-center font-bold text-white shadow-inner text-base">
                {agentProfile.agentName.charAt(0)}
              </div>
            )}
            <div>
              <h1 className="text-base sm:text-lg font-bold text-text-primary tracking-tight">
                Schedule Meeting with {agentProfile.agentName}
              </h1>
              <p className="text-xs text-text-tertiary">{agentProfile.agentTitle || 'Team Member'}</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-text-tertiary font-medium bg-canvas-elevated px-3 py-1.5 rounded-lg border border-border-subtle">
            <Globe className="h-3.5 w-3.5 text-text-tertiary" />
            <span>Timezone: {userTimezone}</span>
          </div>
        </div>

        <div className="p-5 sm:p-8 space-y-6">
          {step === 'verify' && (
            <div className="max-w-md mx-auto space-y-5 py-2">
              <div className="text-center space-y-1">
                <h2 className="text-lg font-extrabold text-slate-900">Verify Candidate Eligibility</h2>
                <p className="text-xs text-slate-500">Enter your application credentials to view available times for {agentProfile.agentName}.</p>
              </div>

              <form onSubmit={handleVerify} className="space-y-4 bg-slate-50/70 p-5 rounded-2xl border border-slate-200">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-slate-400" /> Full Name
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Jane Doe"
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-slate-400" /> Email Address <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="e.g. applicant@domain.com"
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 text-slate-400" /> Application ID <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.applicationId}
                    onChange={e => setForm({ ...form, applicationId: e.target.value })}
                    placeholder="e.g. 100020203"
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white font-mono"
                  />
                  <p className="text-[11px] leading-snug text-slate-500">Your numeric Application ID, shown on your application in the portal.</p>
                </div>

                {verifyError && (
                  <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-start gap-2.5">
                    <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                    <p>{verifyError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={verifying}
                  className="w-full py-3 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl text-xs font-extrabold uppercase tracking-wider transition cursor-pointer shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Verifying Credentials...
                    </>
                  ) : (
                    <>
                      Verify & Access Calendar <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {step === 'select_slot' && (
            <div className="space-y-6">
              {/* Candidate Info Header */}
              <div className="flex flex-wrap items-center justify-between p-4 bg-indigo-50/60 border border-indigo-100 rounded-2xl gap-3">
                <div>
                  <p className="text-sm font-extrabold text-indigo-950">{applicantName}</p>
                  <p className="text-xs text-indigo-700 font-mono">Application ID: {canonicalAppId}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-slate-600 bg-white px-3 py-1.5 rounded-xl border border-indigo-100 shadow-2xs">
                    Host: {agentProfile.agentName}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStep('verify')}
                    className="text-xs font-bold text-indigo-600 hover:underline cursor-pointer"
                  >
                    Change Credentials
                  </button>
                </div>
              </div>

              {/* Calendly Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrevDate}
                    className="p-2 border border-slate-200 rounded-xl hover:bg-slate-100 transition cursor-pointer"
                    title="Previous"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-600" />
                  </button>
                  <span className="text-xs sm:text-sm font-extrabold text-slate-800 min-w-32 text-center">
                    {viewMode === 'day' && currentDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {viewMode === 'week' && `Week of ${visibleDates[0]?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                    {viewMode === 'month' && currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextDate}
                    className="p-2 border border-slate-200 rounded-xl hover:bg-slate-100 transition cursor-pointer"
                    title="Next"
                  >
                    <ChevronRight className="h-4 w-4 text-slate-600" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentDate(new Date())}
                    className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-100 transition cursor-pointer"
                  >
                    Today
                  </button>
                </div>

                {/* View Switcher Tabs */}
                <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setViewMode('day')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                      viewMode === 'day' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('week')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                      viewMode === 'week' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('month')}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                      viewMode === 'month' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    Month
                  </button>
                </div>
              </div>

              {/* Slot Calendar Grid */}
              {loadingSlots ? (
                <div className="py-16 text-center text-slate-400 space-y-2">
                  <Loader2 className="h-7 w-7 animate-spin mx-auto text-indigo-600" />
                  <p className="text-xs font-semibold">Loading availability for {agentProfile.agentName}...</p>
                </div>
              ) : slots.length === 0 ? (
                <div className="py-12 px-4 text-center bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                  <Clock className="h-8 w-8 text-slate-300 mx-auto" />
                  <p className="text-xs font-bold text-slate-700">No available time slots</p>
                  <p className="text-xs text-slate-500">There are no open slots matching this team member's schedule.</p>
                </div>
              ) : viewMode === 'month' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-7 gap-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                      <div key={d} className="text-center text-[11px] font-extrabold text-slate-400 uppercase tracking-wider py-1">
                        {d}
                      </div>
                    ))}
                    {visibleDates.map(date => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const dayStr = String(date.getDate()).padStart(2, '0');
                      const dateKey = `${year}-${month}-${dayStr}`;
                      const daySlots = groupedSlotsByDate[dateKey] || [];
                      const isToday = new Date().toDateString() === date.toDateString();
                      const isCurrentMonth = date.getMonth() === currentDate.getMonth();

                      return (
                        <div
                          key={dateKey}
                          className={`border rounded-2xl p-2.5 min-h-24 flex flex-col justify-between transition ${
                            !isCurrentMonth ? 'opacity-40 bg-slate-50/50' :
                            isToday ? 'border-indigo-500 bg-indigo-50/30 ring-1 ring-indigo-500/20' :
                            daySlots.length > 0 ? 'border-slate-200 bg-white hover:border-indigo-200 hover:shadow-2xs' :
                            'border-slate-100 bg-slate-50/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-black ${isToday ? 'text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded-md' : 'text-slate-700'}`}>
                              {date.getDate()}
                            </span>
                            {daySlots.length > 0 && (
                              <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">
                                {daySlots.length} slot{daySlots.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 space-y-1 max-h-28 overflow-y-auto pr-0.5">
                            {daySlots.map(s => {
                              const isSelected = selectedSlot === s.dateTime;
                              const intervalLabel = formatSlotInterval(s.dateTime, s.durationMinutes);
                              return (
                                <button
                                  key={s.dateTime}
                                  type="button"
                                  onClick={() => setSelectedSlot(s.dateTime)}
                                  className={`w-full text-left py-1 px-1.5 rounded-lg border transition text-[10px] font-bold block truncate cursor-pointer ${
                                    isSelected
                                      ? 'border-brand-primary bg-brand-primary text-white shadow-2xs'
                                      : 'border-slate-200 bg-slate-50 hover:bg-indigo-50 text-slate-800'
                                  }`}
                                  title={intervalLabel}
                                >
                                  {intervalLabel}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`grid gap-3 ${
                    viewMode === 'day' ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-7'
                  }`}>
                    {visibleDates.map(date => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const dayStr = String(date.getDate()).padStart(2, '0');
                      const dateKey = `${year}-${month}-${dayStr}`;
                      const daySlots = groupedSlotsByDate[dateKey] || [];
                      const isToday = new Date().toDateString() === date.toDateString();

                      return (
                        <div
                          key={dateKey}
                          className={`border rounded-2xl p-3 flex flex-col min-h-36 ${
                            isToday ? 'border-indigo-300 bg-indigo-50/20' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="border-b border-slate-100 pb-2 mb-2 text-center">
                            <span className="text-[11px] font-bold text-slate-400 block uppercase">
                              {date.toLocaleDateString(undefined, { weekday: 'short' })}
                            </span>
                            <span className={`text-sm font-extrabold ${isToday ? 'text-indigo-600' : 'text-slate-800'}`}>
                              {date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                          </div>

                          <div className="flex-1 space-y-1.5 overflow-y-auto max-h-56 pr-0.5">
                            {daySlots.length === 0 ? (
                              <p className="text-[10px] text-slate-400 italic text-center py-3">No slots</p>
                            ) : (
                              daySlots.map(s => {
                                const isSelected = selectedSlot === s.dateTime;
                                const intervalLabel = formatSlotInterval(s.dateTime, s.durationMinutes);

                                return (
                                  <button
                                    key={s.dateTime}
                                    type="button"
                                    onClick={() => setSelectedSlot(s.dateTime)}
                                    className={`w-full py-2 px-2.5 rounded-xl border text-center transition cursor-pointer flex flex-col items-center justify-center ${
                                      isSelected
                                        ? 'border-brand-primary bg-brand-primary text-white font-bold shadow-xs'
                                        : 'border-slate-200 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 text-slate-800'
                                    }`}
                                  >
                                    <span className="text-[11px] font-bold tracking-tight">
                                      {intervalLabel}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedSlot && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between animate-fade-in">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-emerald-950">Selected Slot Interval</p>
                      <p className="text-xs text-emerald-800 font-semibold">
                        {new Date(selectedSlot).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} @ {formatSlotInterval(selectedSlot, slotDuration)}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-extrabold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-xl">
                    Ready to Book
                  </span>
                </div>
              )}

              {bookingError && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-start gap-2.5">
                  <AlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
                  <p>{bookingError}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  disabled={booking || !selectedSlot}
                  onClick={handleBook}
                  className="w-full py-3 bg-brand-primary hover:bg-brand-primary-dark disabled:bg-slate-300 text-white rounded-xl text-xs font-extrabold uppercase tracking-wider transition cursor-pointer shadow-md flex items-center justify-center gap-2"
                >
                  {booking ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Reserving Appointment...
                    </>
                  ) : (
                    `Confirm Appointment with ${agentProfile.agentName}`
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'confirmation' && (
            <div className="py-6 text-center space-y-5 max-w-lg mx-auto">
              <div className="h-14 w-14 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-sm">
                <CheckCircle className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">Appointment Confirmed!</h2>
                <p className="text-xs text-slate-600 mt-1">
                  A confirmation email with meeting details has been sent to <strong>{form.email}</strong>.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-left space-y-2.5 text-xs">
                <p className="font-bold text-slate-800 border-b border-slate-200 pb-2 text-sm">Appointment Summary</p>
                <p><span className="text-slate-400">Applicant:</span> <strong>{applicantName}</strong></p>
                <p><span className="text-slate-400">Application ID:</span> <strong>{canonicalAppId}</strong></p>
                <p><span className="text-slate-400">Time Slot:</span> <strong>{new Date(bookedDetails?.dateTime).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} ({formatSlotInterval(bookedDetails?.dateTime, slotDuration)})</strong></p>
                <p><span className="text-slate-400">Assigned Agent:</span> <strong>{bookedDetails?.assignedAgent || agentProfile.agentName}</strong></p>
                {bookedDetails?.meetingUrl && (
                  <div className="pt-2 border-t border-slate-200">
                    <span className="text-slate-400 block mb-1">Video Meeting Room:</span>
                    <a
                      href={bookedDetails.meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-2xs"
                    >
                      Join Screening Meeting Room &rarr;
                    </a>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-500">
                You can view meeting instructions inside your portal account under <strong>My Events</strong>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
